import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import {
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";
import { createColumns } from "./columns";
// import { useModifiedCells } from "../../hooks/useModifiedCells"; // modified cell tracking removed - table is now read-only
// import { processRowUpdate, findChangedFields } from "../../utils/tableHelpers"; // edit helpers commented out - table is now read-only
import "../../style/MaterialsTable.css";

// handles the behaviors of the List of Materials table, including input validation, and modifications
const MaterialsTable = forwardRef(
  (
    {
      data,
      onRowSelect,
      darkMode,
      onDataUpdate,
      height = 600,
      onError,
      onSelectionChange,
      externalSelection,
      columnVisibility = {}, // column visibility settings
    },
    ref,
  ) => {
    const { t } = useTranslation();

    // getCellClassName returns empty string (no cell highlighting needed) // modified cell tracking removed - table is now read-only
    const getCellClassName = useCallback(() => "", []);

    const [selectedRows, setSelectedRows] = useState([]);
    const lastSelectedIndexRef = useRef(null);

    useImperativeHandle(ref, () => ({})); // clearModifiedCells removed - no edits to clear

    // sync local state with external selection
    useEffect(() => {
      setSelectedRows(externalSelection || []);
    }, [externalSelection]);

    // create rows with selection state embedded
    const rowsWithSelection = useMemo(() => {
      return data.map((row) => ({
        ...row,
        __selected: selectedRows.includes(row.id),
      }));
    }, [data, selectedRows]);

    // Cells are no longer editable, but text can be selected and copied // edit handlers commented out - table is now read-only
    /*
    const handleProcessRowUpdate = (newRow, oldRow) => {
      try {
        const processedNewRow = processRowUpdate(newRow, oldRow);

        // ensure quantities are valid numbers (only validate if these fields were edited)
        if (
          isNaN(processedNewRow.remaining_qty) ||
          isNaN(processedNewRow.quantity) ||
          processedNewRow.remaining_qty === null ||
          processedNewRow.quantity === null ||
          processedNewRow.remaining_qty === "" ||
          processedNewRow.quantity === ""
        ) {
          const error = new Error(t("Quantities must be valid numbers"));
          if (onError) {
            onError(error);
          }
          return oldRow;
        }

        // ensure both quantities are non-negative
        if (processedNewRow.remaining_qty < 0 || processedNewRow.quantity < 0) {
          const error = new Error(t("Quantities cannot be negative"));
          if (onError) {
            onError(error);
          }
          return oldRow;
        }

        // ensure remaining_qty <= quantity
        if (processedNewRow.remaining_qty > processedNewRow.quantity) {
          const error = new Error(
            t(
              "You cannot provide a Quantity Left entry that exceeds the Quantity Total. Please try again."
            )
          );
          if (onError) {
            onError(error);
          }
          return oldRow;
        }

        const changedFields = findChangedFields(processedNewRow, oldRow);

        if (changedFields.length > 0) {
          const cellKeys = changedFields.map(
            (field) => `${processedNewRow.id}-${field}`
          );
          // Pass the processed row so hook can compare with original
          markCellsAsModified(cellKeys, processedNewRow);

          const updatedData = data.map((row) =>
            row.id === processedNewRow.id ? processedNewRow : row
          );
          onDataUpdate(updatedData);
        }

        return processedNewRow;
      } catch (error) {
        console.error("Error processing row update:", error);
        if (onError) {
          onError(error);
        }
        return oldRow;
      }
    };

    const handleProcessRowUpdateError = (error) => {
      console.error("Edit error:", error);
      if (onError) {
        onError(error);
      }
    };
    */

    const handleRowClick = (params, event) => {
      const clickedIndex = data.findIndex((row) => row.id === params.id);

      let newSelection;

      if (event.shiftKey && lastSelectedIndexRef.current !== null) {
        // prevent text selection when shift-clicking
        event.preventDefault();

        const start = Math.min(lastSelectedIndexRef.current, clickedIndex);
        const end = Math.max(lastSelectedIndexRef.current, clickedIndex);
        newSelection = data.slice(start, end + 1).map((row) => row.id);
      } else if (event.ctrlKey || event.metaKey) {
        newSelection = selectedRows.includes(params.id)
          ? selectedRows.filter((id) => id !== params.id)
          : [...selectedRows, params.id];
        lastSelectedIndexRef.current = clickedIndex;
      } else {
        newSelection = [params.id];
        lastSelectedIndexRef.current = clickedIndex;
      }

      // update local state immediately for visual feedback
      setSelectedRows(newSelection);

      // notify parent
      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }

      // call onRowSelect with the clicked row
      onRowSelect(params.row);
    };

    const columns = useMemo(
      () => createColumns(
        t,
        getCellClassName,
        darkMode,
        columnVisibility,
        selectedRows.length,
        data.length,
        () => {
          const allSelected = selectedRows.length === data.length && data.length > 0;
          const next = allSelected ? [] : data.map((r) => r.id);
          setSelectedRows(next);
          if (onSelectionChange) onSelectionChange(next);
        },
      ),
      [t, getCellClassName, darkMode, columnVisibility, selectedRows.length, data, onSelectionChange],
    );

    const dataGridStyles = useMemo(
      () => ({
        border: "none",
        height,
        userSelect: "text", // allow text selection for copying
        "& .MuiDataGrid-virtualScroller": {
          overflowY: "auto",
        },
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "rgba(102, 157, 46, 0.15)",
        },
        "& .MuiDataGrid-row.Mui-selected": {
          backgroundColor: "rgba(102, 157, 46, 0.3) !important",
        },
        "& .MuiDataGrid-row.Mui-selected:hover": {
          backgroundColor: "rgba(102, 157, 46, 0.4) !important",
        },
        "& .MuiDataGrid-cell": {
          cursor: "text", // text cursor for all cells (read-only but copyable)
          userSelect: "text", // allow text selection in all cells
        },
        "& .MuiDataGrid-cell:focus-within": {
          outline: "none",
        },
        "& .MuiDataGrid-columnHeader:focus": {
          outline: "none",
        },
        "& .MuiDataGrid-columnHeader:focus-within": {
          outline: "none",
        },
        "& .MuiDataGrid-columnSeparator": {
          display: "none",
        },
      }),
      [height],
    );

    // Add getRowClassName to highlight selected rows
    const getRowClassName = useCallback((params) => {
      return params.row.__selected ? "row-is-selected" : "";
    }, []);

    const finalStyles = useMemo(() => {
      const styles = {
        ...dataGridStyles,
        "& .MuiDataGrid-row.row-is-selected": {
          borderLeft: "4px solid #669d2e !important",
        },
      };

      selectedRows.forEach((id) => {
        styles[`& .MuiDataGrid-row[data-id="${id}"]`] = {
          borderLeft: "4px solid #669d2e !important",
        };
      });

      return styles;
    }, [dataGridStyles, selectedRows]);

    return (
      <DataGrid
        rows={rowsWithSelection}
        columns={columns}
        disableColumnResize
        disableColumnMenu
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        disableRowSelectionOnClick
        getRowClassName={getRowClassName}
        onRowClick={handleRowClick}
        sortingOrder={["asc", "desc"]}
        className="materials-table"
        sx={finalStyles}
      />
    );
  },
);

MaterialsTable.displayName = "MaterialsTable";

export default MaterialsTable;
